// TurnstileProvider.tsx
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
} from "react";
import Turnstile, { BoundTurnstileObject } from "react-turnstile";

const SITE_KEY = "0x4AAAAAACGy1W8q46cDFiH5";

type BoundTurnstile = {
    execute: () => void;
    reset: () => void;
};

type Ctx = {
    getToken: () => Promise<string>;
};

const TurnstileCtx = createContext<Ctx | null>(null);

export function useTurnstileToken() {
    const ctx = useContext(TurnstileCtx);
    if (!ctx) throw new Error("TurnstileProvider missing");
    return ctx.getToken;
}

function TurnstileHiddenWidget({
    setBound,
    onToken,
    onFail,
}: {
    setBound: (b: BoundTurnstile | null) => void;
    onToken: (t: string, bound: BoundTurnstileObject) => void;
    onFail: (e: Error, bound?: BoundTurnstileObject) => void;
}) {
    return (
        <div
            style={{
                position: "fixed",
                left: -9999,
                bottom: 0,
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: "none",
            }}
        >
            <Turnstile
                sitekey={SITE_KEY}
                appearance="execute"
                execution="execute"
                refreshExpired="auto"
                action="quiz_event"

                onLoad={(_id: string, bound: BoundTurnstileObject) => {
                    setBound(bound);
                }}

                // ✅ single callback for success
                onVerify={(token: string, bound: BoundTurnstileObject) => {
                    onToken(token, bound);
                }}

                onError={(error?: any, bound?: BoundTurnstileObject) => {
                    onFail(new Error(error?.message ?? "Turnstile error"), bound);
                }}

                onExpire={(_token: string, bound: BoundTurnstileObject) => {
                    onFail(new Error("Turnstile expired"), bound);
                }}

                onTimeout={(bound: BoundTurnstileObject) => {
                    onFail(new Error("Turnstile timeout"), bound);
                }}

                onUnsupported={(bound: BoundTurnstileObject) => {
                    onFail(new Error("Turnstile unsupported"), bound);
                }}
            />
        </div>
    );
}

export function TurnstileProvider({ children }: { children: React.ReactNode }) {
    const boundRef = useRef<BoundTurnstile | null>(null);

    const inFlight = useRef<Promise<string> | null>(null);
    const doneRef = useRef(false);
    const resolveRef = useRef<((t: string) => void) | null>(null);
    const rejectRef = useRef<((e: Error) => void) | null>(null);
    const timerRef = useRef<number | null>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current != null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const cleanup = useCallback(() => {
        clearTimer();
        inFlight.current = null;
        resolveRef.current = null;
        rejectRef.current = null;
        doneRef.current = false;
    }, [clearTimer]);

    const setBound = useCallback((b: BoundTurnstile | null) => {
        boundRef.current = b;
    }, []);

    const fail = useCallback(
        (e: Error, bound?: BoundTurnstileObject) => {
            if (doneRef.current) return;
            doneRef.current = true;

            try {
                bound?.reset?.();
                boundRef.current?.reset?.();
            } catch { }

            rejectRef.current?.(e);
            cleanup();
        },
        [cleanup]
    );

    const succeed = useCallback(
        (t: string, bound: BoundTurnstileObject) => {
            if (doneRef.current) return;
            doneRef.current = true;

            // Optional: reset after success so next execute is clean
            try {
                bound.reset();
            } catch { }

            resolveRef.current?.(t);
            cleanup();
        },
        [cleanup]
    );

    const getToken = useCallback(() => {
        if (inFlight.current) return inFlight.current;

        inFlight.current = new Promise<string>((resolve, reject) => {
            resolveRef.current = resolve;
            rejectRef.current = reject;
            doneRef.current = false;

            // hard timeout
            timerRef.current = window.setTimeout(() => {
                fail(new Error("Turnstile timeout (client)"));
            }, 12000);

            if (!boundRef.current) {
                fail(new Error("Turnstile not loaded yet"));
                return;
            }

            try {
                // Clean slate, then execute
                boundRef.current.reset();
                boundRef.current.execute();
            } catch (e: any) {
                fail(new Error(e?.message ?? "Turnstile execute failed"));
            }
        });

        return inFlight.current;
    }, [fail]);

    const value = useMemo(() => ({ getToken }), [getToken]);

    return (
        <TurnstileCtx.Provider value={value}>
            <TurnstileHiddenWidget
                setBound={setBound}
                onToken={succeed}
                onFail={fail}
            />
            {children}
        </TurnstileCtx.Provider>
    );
}